import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Authenticated from '../../layout/Authenticated';
import { ArrowLeft, Calendar, FileText, IndianRupee, User, Clock, CheckCircle2, XCircle, RefreshCcw, Edit2, RotateCcw } from 'lucide-react';
import axiosInstance from '../../api/axiosInstance';

const SalesReturnDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [salesReturn, setSalesReturn] = useState(null);
  const [loading, setLoading] = useState(true);

  // useEffect(() => {
  //   fetchSalesReturnDetails();
  // }, [id]);

  useEffect(() => {
    const fetchSalesReturnDetails = async () => {
      try {
        setLoading(true);
        // ✅ FIX: Call specific API endpoint instead of fetching all returns
        const response = await axiosInstance.get(`/sales-returns/${id}`);
        setSalesReturn(response.data);
      } catch (error) {
        console.error('Error fetching sales return details:', error);
        setSalesReturn(null);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchSalesReturnDetails();
    }
  }, [id]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: Clock },
      APPROVED: { bg: 'bg-green-100', text: 'text-green-800', icon: CheckCircle2 },
      REJECTED: { bg: 'bg-red-100', text: 'text-red-800', icon: XCircle },
      PROCESSED: { bg: 'bg-blue-100', text: 'text-blue-800', icon: FileText },
      REFUNDED: { bg: 'bg-teal-100', text: 'text-teal-800', icon: RefreshCcw }
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        <IconComponent size={14} className="mr-1" />
        {status}
      </span>
    );
  };

  const getReasonBadge = (reason) => {
    if (!reason) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">Not specified</span>;
    }

    const reasonConfig = {
      DEFECTIVE: { bg: 'bg-red-100', text: 'text-red-700' },
      EXPIRED: { bg: 'bg-orange-100', text: 'text-orange-700' },
      WRONG_MEDICINE: { bg: 'bg-purple-100', text: 'text-purple-700' },
      SIDE_EFFECTS: { bg: 'bg-pink-100', text: 'text-pink-700' },
      DOCTOR_CHANGE: { bg: 'bg-blue-100', text: 'text-blue-700' },
      DUPLICATE_PURCHASE: { bg: 'bg-indigo-100', text: 'text-indigo-700' },
      OTHER: { bg: 'bg-gray-100', text: 'text-gray-700' }
    };

    const config = reasonConfig[reason] || reasonConfig.OTHER;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {reason.replace(/_/g, ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <Authenticated>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
        </div>
      </Authenticated>
    );
  }

  if (!salesReturn) {
    return (
      <Authenticated>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <RotateCcw className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Sales return not found</h3>
          </div>
        </div>
      </Authenticated>
    );
  }

  return (
    <Authenticated>
      <div className="min-h-screen bg-gray-50 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => navigate('/sales-return-list')}
                  className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="bg-teal-600 p-2 rounded-lg">
                  <RotateCcw className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Return #{salesReturn.id}</h1>
                  <p className="text-sm text-gray-600 mt-1">Sales return details</p>
                </div>
              </div>
              {salesReturn.return_status === 'PENDING' && (
                <button
                  onClick={() => navigate(`/sales-returns/${id}/status`)}
                  className="inline-flex items-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg shadow-md hover:shadow-lg transition-all duration-200"
                >
                  <Edit2 size={18} className="mr-2" />
                  Update Status
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Return Information */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Return Information</h2>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Customer</label>
                    <div className="flex items-center">
                      <User size={16} className="text-gray-400 mr-2" />
                      <div>
                        <div className="font-medium text-gray-900">{salesReturn.customer_name}</div>
                        <div className="text-sm text-gray-500">{salesReturn.customer_mobile}</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Original Invoice</label>
                    <div className="flex items-center">
                      <FileText size={16} className="text-gray-400 mr-2" />
                      <div>
                        <div className="font-medium text-gray-900">Invoice #{salesReturn.original_invoice_id}</div>
                        {salesReturn.originalinvoice && (
                          <div className="text-sm text-gray-500">
                            {new Date(salesReturn.originalinvoice.invoicedate).toLocaleDateString()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Return Date</label>
                    <div className="flex items-center">
                      <Calendar size={16} className="text-gray-400 mr-2" />
                      <span className="text-gray-900">{new Date(salesReturn.return_date).toLocaleDateString()}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
                    {getStatusBadge(salesReturn.return_status)}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Return Reason</label>
                    {getReasonBadge(salesReturn.return_reason)}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Prescription Returned</label>
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${salesReturn.prescription_returned
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                      }`}>
                      {salesReturn.prescription_returned ? 'Yes' : 'No'}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-500 mb-1">Total Return Amount</label>
                    <div className="flex items-center">
                      <IndianRupee size={16} className="text-gray-400 mr-1" />
                      <span className="text-lg font-semibold text-gray-900">
                        ₹{parseFloat(salesReturn.total_return_amt).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {parseFloat(salesReturn.refund_amount) > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-500 mb-1">Refund Amount</label>
                      <div className="flex items-center">
                        <IndianRupee size={16} className="text-green-500 mr-1" />
                        <span className="text-lg font-semibold text-green-600">
                          ₹{parseFloat(salesReturn.refund_amount).toFixed(2)}
                        </span>
                      </div>
                      {salesReturn.refund_method && (
                        <div className="text-sm text-gray-500 mt-1">
                          via {salesReturn.refund_method.replace('_', ' ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {salesReturn.notes && (
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <label className="block text-sm font-medium text-gray-500 mb-2">Notes</label>
                    <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">{salesReturn.notes}</p>
                  </div>
                )}
              </div>

              {/* Return Items */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Returned Items</h2>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Medicine
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Batch & Expiry
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Reason
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Condition
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {salesReturn.returnitems?.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {item.medicine.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {item.medicine.generic_name}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              <div>Batch: {item.batch_number}</div>
                              <div className="text-gray-500">
                                Exp: {new Date(item.expiry_date).toLocaleDateString()}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {item.return_quantity} units
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            {getReasonBadge(item.return_reason)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.is_medicine_intact
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                              }`}>
                              {item.is_medicine_intact ? 'Intact' : 'Damaged'}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end">
                              <IndianRupee size={14} className="text-gray-400 mr-1" />
                              <span className="text-sm font-medium text-gray-900">
                                ₹{parseFloat(item.return_value).toFixed(2)}
                              </span>
                            </div>
                            <div className="text-xs text-gray-500">
                              ₹{parseFloat(item.original_price_per_unit).toFixed(2)}/unit
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Summary Card */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Summary</h3>

                <div className="space-y-4">
                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Items Returned</span>
                    <span className="font-medium">{salesReturn.returnitems?.length || 0}</span>
                  </div>

                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Total Units</span>
                    <span className="font-medium">
                      {salesReturn.returnitems?.reduce((sum, item) => sum + item.return_quantity, 0) || 0}
                    </span>
                  </div>

                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-sm text-gray-600">GST Amount</span>
                    <span className="font-medium">₹{parseFloat(salesReturn.total_gst_amount || 0).toFixed(2)}</span>
                  </div>

                  <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Intact Items</span>
                    <span className="font-medium text-green-600">
                      {salesReturn.returnitems?.filter(item => item.is_medicine_intact).length || 0}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-lg font-semibold text-teal-600">
                    <span>Total Return Value</span>
                    <div className="flex items-center">
                      <IndianRupee size={18} className="mr-1" />
                      ₹{parseFloat(salesReturn.total_return_amt).toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              {(salesReturn.processing_date || salesReturn.refund_date) && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h3>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-3">
                      <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Return Created</p>
                        <p className="text-xs text-gray-500">
                          {new Date(salesReturn.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {salesReturn.processing_date && (
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Processed</p>
                          <p className="text-xs text-gray-500">
                            {new Date(salesReturn.processing_date).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}

                    {salesReturn.refund_date && (
                      <div className="flex items-start space-x-3">
                        <div className="flex-shrink-0 w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">Refunded</p>
                          <p className="text-xs text-gray-500">
                            {new Date(salesReturn.refund_date).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Authenticated>
  );
};

export default SalesReturnDetails;